import {
  DescriptionOutlined,
  ShowChartOutlined,
  PeopleOutline,
  WarningAmberOutlined,
  CheckCircleOutlined,
  ShieldOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import { CardContainer, MetricCard, Tag, Button } from 'finara-components';
import React from 'react';

import { Navbar } from '@/components/admin/general/Navbar';
import { CsvExportButton } from '@/components/common';
import {
  auditMetrics,
  recentEvents,
  securityStatus,
  recentAccessAttempts,
  exportOptions,
} from '@/data/admin/audit/auditData';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { useAuth } from '@/hooks/useAuth';

export const AuditPage: React.FC = () => {
  const { user } = useAuth();

  const getSeverityVariant = (severity: string): 'green' | 'yellow' | 'red' => {
    switch (severity) {
      case 'Normal':
        return 'green';
      case 'Media':
        return 'yellow';
      case 'Alta':
        return 'red';
      default:
        return 'green';
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar
        navItems={adminNavItems}
        userName={user?.name || 'Usuario'}
        pageTitle="Auditoría Empresarial"
        section="Auditoría"
      />

      <div className="px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DescriptionOutlined />
            Auditoría Empresarial
          </h1>
          <p className="text-gray-600 mt-1">
            Bitácora completa de operaciones, cambios de configuración y accesos de usuarios de tu
            empresa.
          </p>
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-1 md:!grid-cols-2 lg:!grid-cols-4 gap-6 mb-6">
          <MetricCard
            title="Eventos del mes"
            value={auditMetrics.monthEvents.toString()}
            icon={<ShowChartOutlined className="text-blue-600" />}
            variant="white"
          />
          <MetricCard
            title="Usuarios activos"
            value={auditMetrics.activeUsers.toString()}
            icon={<PeopleOutline className="text-green-600" />}
            variant="white"
          />
          <MetricCard
            title="Alertas críticas"
            value={auditMetrics.criticalAlerts.toString()}
            icon={<WarningAmberOutlined className="text-red-600" />}
            variant="white"
          />
          <MetricCard
            title="Tasa de cumplimiento"
            value={`${auditMetrics.complianceRate}%`}
            icon={<CheckCircleOutlined className="text-green-600" />}
            variant="white"
          />
        </div>

        <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
          {/* Left Column - Recent Events */}
          <div>
            <CardContainer>
              <div className="flex items-center gap-2 mb-6">
                <ShowChartOutlined className="text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">Eventos Recientes</h3>
              </div>

              <div className="space-y-3">
                {recentEvents.map(event => (
                  <div
                    key={event.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{event.title}</p>
                        <p className="text-sm text-gray-500">{event.user}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {event.timestamp} Por: {event.author}
                        </p>
                      </div>
                      <Tag variant={getSeverityVariant(event.severity)}>{event.severity}</Tag>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 text-center">
                <Button variant="white" size="small" startIcon={<DescriptionOutlined />}>
                  Ver Registro Completo
                </Button>
              </div>
            </CardContainer>
          </div>

          {/* Right Column - Security & Access */}
          <div>
            <CardContainer>
              <div className="flex items-center gap-2 mb-6">
                <ShieldOutlined className="text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">Seguridad y Accesos</h3>
              </div>

              {/* Security Status */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Estado de Seguridad</h4>
                <p className="text-sm text-gray-700 mb-4">{securityStatus.status}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">
                      Usuarios autenticados: {securityStatus.authenticatedUsers}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      Sesiones activas: {securityStatus.activeSessions}
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent Access Attempts */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                  Intentos de Acceso Recientes
                </h4>
                <div className="space-y-3">
                  {recentAccessAttempts.map(attempt => (
                    <div
                      key={attempt.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {attempt.status === 'Exitoso' ? (
                          <CheckCircleOutlined className="text-green-600" fontSize="small" />
                        ) : (
                          <WarningAmberOutlined className="text-red-600" fontSize="small" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{attempt.user}</p>
                          <p className="text-xs text-gray-500">
                            {attempt.status} - {attempt.timestamp}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContainer>
          </div>

          {/* Configuration Section */}
          <CardContainer className="mt-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <SettingsOutlined className="text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Configuración de Auditoría</h3>
            </div>

            <div className="grid grid-cols-1 md:!grid-cols-3 gap-6">
              {exportOptions.map((option, index) => (
                <CardContainer key={index}>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">{option.title}</h4>
                  <p className="text-sm text-gray-600 mb-3">{option.description}</p>
                  {index === exportOptions.length - 1 && (
                    <CsvExportButton
                      data={recentEvents}
                      filename="auditoria"
                      columns={event => ({
                        ID: event.id,
                        Título: event.title,
                        Usuario: event.user,
                        Autor: event.author,
                        'Fecha y Hora': event.timestamp,
                        Severidad: event.severity,
                      })}
                      variant="white"
                      size="small"
                      buttonText="Exportar"
                    />
                  )}
                  {index < exportOptions.length - 1 && (
                    <Button variant="white" size="small">
                      Configurar
                    </Button>
                  )}
                </CardContainer>
              ))}
            </div>
          </CardContainer>
        </div>
      </div>
    </div>
  );
};
